import { Divider } from "@mui/material";
import _ from "lodash";
import { useMemo } from "react";

import { validateProviderNpi } from "../../helper/functions";
import useModifyProviderDetails from "../../hooks/useModifyProviderDetails";
import useProviderContractDetails from "../../hooks/useProviderContractDetails";
import useProviderParentEntityOptions from "../../hooks/useProviderParentEntityOptions";
import { useProviderDetailStore } from "../../store/provider-details/providerDetailStore";
import CancelButton from "../buttons/CancelButton";
import SaveButton from "../buttons/SaveButton";
import CustomTextField from "../fields/CustomTextField";
import SingleSelectDropdown from "../fields/single-select-dropdown-field/SingleSelectDropdown";
import FieldLabel from "../provider-details/FieldLabel";

import ParentEntityLink from "./ParentEntityLink";

const getCurrentParentOption = (parent) => {
  if (!parent) {
    return null;
  }

  const id = parent?.id ?? parent?.pk ?? parent;
  const label = parent?.name ?? parent?.value ?? parent?.label ?? "";

  return id && label ? { id, label } : null;
};

const ENTITY_INFO_FIELDS = [
  "npi",
  "nps",
  "entity",
  "contractEntity",
  "ein",
  "contractStartDate",
  "contractEndDate",
];

export default function EntityInfo() {
  const { data, initialData, modifiedData, setModifiedData } = useProviderDetailStore();
  const { handleFieldOnChangeValue, handleOnClickSaveButton } = useModifyProviderDetails();
  const { options } = useProviderParentEntityOptions();
  useProviderContractDetails();

  const parentEntityOptions = useMemo(() => {
    const currentParentOption =
      getCurrentParentOption(data?.parentEntityOption) ?? getCurrentParentOption(data?.entity);

    if (!currentParentOption || options.some((option) => option.id === currentParentOption.id)) {
      return options;
    }

    return [currentParentOption, ...options];
  }, [data?.entity, data?.parentEntityOption, options]);

  const selectedParentEntity = useMemo(() => {
    const selectedOption = parentEntityOptions.find(
      (option) => String(option.id) === String(modifiedData?.entity)
    );

    return (
      selectedOption ??
      getCurrentParentOption(data?.parentEntityOption) ??
      getCurrentParentOption(data?.entity)
    );
  }, [data?.entity, data?.parentEntityOption, modifiedData?.entity, parentEntityOptions]);

  const isDirty = useMemo(() => {
    const initialValues = _.pick(initialData, ENTITY_INFO_FIELDS);
    const modifiedValues = _.pick(modifiedData, ENTITY_INFO_FIELDS);

    return !_.isEqual(initialValues, modifiedValues);
  }, [initialData, modifiedData]);
  const npiError = useMemo(() => validateProviderNpi(modifiedData?.npi), [modifiedData?.npi]);

  const handleOnClickCancelButton = () => {
    setModifiedData({ ...modifiedData, ..._.pick(initialData, ENTITY_INFO_FIELDS) });
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto pr-2 [&::-webkit-scrollbar-thumb]:bg-blue-700 [&::-webkit-scrollbar]:[width:6px]">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-1">
          <FieldLabel value="National Provider ID (NPI)" />
          <CustomTextField
            fieldName="npi"
            fieldValue={modifiedData?.npi ?? ""}
            onChange={handleFieldOnChangeValue}
            error={Boolean(npiError)}
            helperText={npiError}
            inputMode="numeric"
            maxLength={10}
          />
        </div>
        <div className="flex flex-col gap-1">
          <FieldLabel value="Net Promoter Score (NPS)" />
          <CustomTextField
            fieldName="nps"
            fieldValue={modifiedData?.nps ?? ""}
            onChange={handleFieldOnChangeValue}
          />
        </div>
      </div>

      <Divider className="my-6" />

      <div className="grid grid-cols-1 gap-4">
        <div className="flex flex-col gap-1">
          <FieldLabel value="Parent Entity" />
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <SingleSelectDropdown
                fieldName="entity"
                fieldValue={modifiedData?.entity ?? null}
                onChange={handleFieldOnChangeValue}
                data={parentEntityOptions}
                placeholder="Select Parent Entity"
              />
            </div>
            {!isDirty ? <ParentEntityLink entity={selectedParentEntity} /> : null}
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-1">
          <FieldLabel value="Contract Entity" />
          <CustomTextField
            fieldName="contractEntity"
            fieldValue={modifiedData?.contractEntity ?? ""}
            onChange={() => {}}
            readOnly
            noClearIcon
          />
        </div>
        <div className="flex flex-col gap-1">
          <FieldLabel value="EIN" />
          <CustomTextField
            fieldName="ein"
            fieldValue={modifiedData?.ein ?? ""}
            onChange={() => {}}
            readOnly
            noClearIcon
          />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-1">
          <FieldLabel value="Contract Start Date" />
          <CustomTextField
            fieldName="contractStartDate"
            fieldValue={modifiedData?.contractStartDate ?? ""}
            onChange={() => {}}
            readOnly
            noClearIcon
          />
        </div>
        <div className="flex flex-col gap-1">
          <FieldLabel value="Contract End Date" />
          <CustomTextField
            fieldName="contractEndDate"
            fieldValue={modifiedData?.contractEndDate ?? ""}
            onChange={() => {}}
            readOnly
            noClearIcon
          />
        </div>
      </div>

      <div className="mt-auto pt-6">
        <div className="ml-auto grid w-full grid-cols-1 gap-4 md:w-1/2 md:grid-cols-2">
          <CancelButton disabled={!isDirty} onClick={handleOnClickCancelButton} />
          <SaveButton
            disabled={!isDirty || Boolean(npiError)}
            onClick={handleOnClickSaveButton}
            label="Save"
          />
        </div>
      </div>
    </div>
  );
}
